
import { createContext, useState, useContext, useEffect, useMemo } from "react";
import StoreSettingService from "services/store_setting";
import PackageService from "services/package";

const service = new StoreSettingService();
const packageService = new PackageService();

const buildWebsiteBaseUrl = (websiteSetting) => {
    const customDomain = String(websiteSetting?.custom_domain || '')
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/\/$/, '');

    if (window.location.host.includes('localhost')) {
        return 'http://localhost:3069';
    }

    if (customDomain) {
        return `https://${customDomain}`;
    }

    if (websiteSetting?.url) {
        return `https://shop${websiteSetting.url}.sipro.app`;
    }

    return '';
};

const buildShopDomainUrl = (websiteSetting) => {
    if (window.location.host.includes('localhost')) {
        return 'http://localhost:3069';
    }

    if (websiteSetting?.url) {
        return `https://shop${websiteSetting.url}.sipro.app`;
    }

    return '';
};

const defaultSubscriptionStatus = {
    canCreateProduct: { canCreate: true, remaining: Infinity, limit: Infinity, current: 0 },
    canCreateOrder: { canCreate: true, remaining: Infinity, limit: Infinity, current: 0 },
    storageStatus: { usedGB: 0, limitGB: Infinity, percentUsed: 0, canUpload: true }
};

/**
 * Compute subscription status validators
 */
const computeSubscriptionStatus = (subscription) => {
    const limits = subscription?.package?.limits || subscription?.current?.limits;
    const usage = subscription?.usage || subscription?.current?.usage;

    if (!limits || !usage) {
        return defaultSubscriptionStatus;
    }
    
    // Product limit check
    const productLimit = Number(limits.product_count || 0);
    const productUsed = Number(usage.product_count || 0);
    const productRemaining = productLimit - productUsed;
    const canCreateProduct = {
        canCreate: productRemaining > 0,
        remaining: Math.max(0, productRemaining),
        limit: productLimit,
        current: productUsed
    };
    
    // Order limit check
    const orderLimit = Number(limits.order_count_month || 0);
    const orderUsed = Number(usage.order_count_month || 0);
    const orderRemaining = orderLimit - orderUsed;
    const canCreateOrder = {
        canCreate: orderRemaining > 0,
        remaining: Math.max(0, orderRemaining),
        limit: orderLimit,
        current: orderUsed
    };
    
    // Storage check — prefer normalized payload from backend when present.
    let storageStatus;
    if (subscription?.storage) {
        const s = subscription.storage;
        const usedGB = (s.used_bytes || 0) / (1024 ** 3);
        const limitGB = s.limit_gb || 0;
        storageStatus = {
            usedGB: parseFloat(usedGB.toFixed(2)),
            limitGB,
            usedBytes: s.used_bytes || 0,
            limitBytes: s.limit_bytes || 0,
            remainingBytes: s.remaining_bytes || 0,
            percentUsed: typeof s.percent === 'number' ? s.percent : (limitGB > 0 ? parseFloat(((usedGB / limitGB) * 100).toFixed(1)) : 0),
            canUpload: limitGB > 0 ? (s.used_bytes < s.limit_bytes) : true,
        };
    } else {
        const storageBytes = Number(usage.storage_used_bytes ?? usage.storage_bytes ?? 0);
        const usedGB = storageBytes / (1024 ** 3);
        const limitGB = Number(limits.storage_gb || 0);
        const percentUsed = limitGB > 0 ? (usedGB / limitGB) * 100 : 0;
        storageStatus = {
            usedGB: parseFloat(usedGB.toFixed(2)),
            limitGB,
            usedBytes: storageBytes,
            limitBytes: limitGB * (1024 ** 3),
            remainingBytes: Math.max(0, limitGB * (1024 ** 3) - storageBytes),
            percentUsed: parseFloat(percentUsed.toFixed(1)),
            canUpload: limitGB > 0 ? usedGB < limitGB : true
        };
    }
    
    return {
        canCreateProduct,
        canCreateOrder,
        storageStatus
    };
};

const WorkspaceContext = createContext();

function WorkspaceProvider({ children }) {

    const [workspace, setWorkSpace] = useState({ 
        collaboratorSetting: {},
        subscriptionStatus: defaultSubscriptionStatus,
        websiteUrl: '',
        shopDomainUrl: ''
    });


    useEffect(() => {
        loadData();

    }, [])

    const loadData = async () => {
        try {
            const [setting, subscriptionData, websiteSetting] = await Promise.all([
                service.getCollaboratorSetting(),
                packageService.getCurrent(),
                service.getWebsiteSetting()
            ]);
            
            const subscriptionStatus = computeSubscriptionStatus(subscriptionData);
            const websiteUrl = buildWebsiteBaseUrl(websiteSetting);
            const shopDomainUrl = buildShopDomainUrl(websiteSetting);
            
            setWorkSpace({ 
                collaboratorSetting: setting,
                subscription: subscriptionData,
                subscriptionStatus,
                websiteUrl,
                shopDomainUrl
            })
        } catch (error) {
            console.error("Error loading workspace data:", error);
            // Set defaults on error
            setWorkSpace({ 
                collaboratorSetting: {},
                subscription: null,
                subscriptionStatus: defaultSubscriptionStatus,
                websiteUrl: buildWebsiteBaseUrl(null),
                shopDomainUrl: buildShopDomainUrl(null)
            })
        }
    }

    /**
     * Refresh just the subscription/usage portion of the workspace,
     * keeping other slices intact. Use after upload/delete to update
     * the storage counter without a full reload.
     */
    const reloadSubscription = async () => {
        try {
            const subscriptionData = await packageService.getCurrent();
            const subscriptionStatus = computeSubscriptionStatus(subscriptionData);
            setWorkSpace((prev) => ({
                ...(prev || {}),
                subscription: subscriptionData,
                subscriptionStatus,
            }));
            return subscriptionData;
        } catch (error) {
            console.error("Error reloading subscription:", error);
            return null;
        }
    };

    const getWebsiteUrl = useMemo(() => {
        return (path = '') => {
            const baseUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');
            if (!baseUrl) {
                return '';
            }

            if (!path) {
                return baseUrl;
            }

            const normalizedPath = String(path).startsWith('/') ? path : `/${path}`;
            return `${baseUrl}${normalizedPath}`;
        };
    }, [workspace?.websiteUrl]);






    return <WorkspaceContext.Provider value={{ workspace, getWebsiteUrl, reloadSubscription }}>
        {children}

    </WorkspaceContext.Provider>;

}

const useWorkSpace = () => {
    const context = useContext(WorkspaceContext);
    return context;
};

export { useWorkSpace };
export default WorkspaceProvider;
